/*  MUI  */
import { Warning } from '@mui/icons-material';
import { AvatarGroup, Box, Grid, TableCell, Typography } from '@mui/material';

/*  Shared  */
import { Percentage, Sum } from 'application/Shared/Components/Cards';
import { DateFormat } from 'application/Shared/Components/Core';
import { DataAPIVisualsPieChart } from 'application/Shared/Components/DataAPIVisualsCard';
import { ManagedTable } from 'application/Shared/Components/Table';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import i18n from 'i18n';

/*  Objects  */
import { Browser } from 'objects/Browser';
import { User, UserFromContext } from 'objects/User';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

const headers = [
    {
        renderHeader: i18n.t('Common.user'),
        width: '40%'
    },
    {
        renderHeader: i18n.t('Common.browsers'),
        width: '20%'
    },
    {
        renderHeader: i18n.t('Common.browser_profiles'),
        width: '40%'
    }
];

const renderBrowsers = (row) => {
    const { id, browsers } = row;

    if (!browsers) {
        return null;
    }

    const render = [];
    browsers.forEach((b) => {
        render.push(
            <Browser key={`${id}_browser_${b.id}`} browser={b} iconOnly />
        );
    });

    return <AvatarGroup spacing="medium">{render}</AvatarGroup>;
};

class Overview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            pieChart: []
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const { t, data } = this.props;
        const { usersWithPersonal, usersWithoutPersonal, counts } = data || {};

        if (!usersWithPersonal || !usersWithoutPersonal || !counts) {
            debug.e('Missing report data', data);
            return;
        }

        const pieChart = [
            {
                color: 'pieChart.personal',
                name: t('Common.personal'),
                value: counts.personal
            },
            {
                color: 'pieChart.domain',
                name: t('Common.domain'),
                value: counts.domain
            },
            {
                color: 'pieChart.na',
                name: t('Common.n/a'),
                value: counts.none
            }
        ];

        this.setState({ loaded: true, pieChart });
    };

    renderCharts = () => {
        const { t, data } = this.props;
        const { pieChart } = this.state;
        const { counts } = data || {};

        return (
            <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={4}>
                    <Sum
                        sum={counts.personal}
                        title={t('Reports.Browser.personal_count')}
                        height={300}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Percentage
                        count={counts.personal}
                        total={counts.total}
                        title={t('Reports.Browser.personal_percent')}
                        height={300}
                    />
                </Grid>
                <Grid item xs={4}>
                    <DataAPIVisualsPieChart
                        card
                        data={pieChart}
                        title={t('Reports.Browser.profile_breakdown')}
                    />
                </Grid>
            </Grid>
        );
    };

    renderAccounts = (row) => {
        const { t } = this.props;
        const { id, report } = row;
        const { accounts } = report;

        if (!accounts.length) {
            return <span>{t('Common.n/a')}</span>;
        }

        const render = [];

        accounts.forEach((a) => {
            const { email, personal, dateUpdated } = a;

            render.push(
                <Box
                    key={`${id}_account_${email}`}
                    display="flex"
                    justifyContent="space-between"
                    width="100%"
                    flexWrap="wrap"
                >
                    <Box>
                        {personal && (
                            <DarkTooltip
                                arrow
                                title={t('Reports.Browser.personal_profile')}
                            >
                                <Warning
                                    fontSize="small"
                                    sx={{ color: 'icon.warning' }}
                                />
                            </DarkTooltip>
                        )}
                        {email}
                    </Box>
                    <Box ml="8px" style={{ float: 'right' }}>
                        <DateFormat date={dateUpdated} />
                    </Box>
                </Box>
            );
        });

        return render;
    };

    renderRow = (row) => (
        <>
            <TableCell>
                <User data={row}>
                    <UserFromContext includeTitle link />
                </User>
            </TableCell>
            <TableCell>{renderBrowsers(row)}</TableCell>
            <TableCell>
                <Box>{this.renderAccounts(row)}</Box>
            </TableCell>
        </>
    );

    renderPersonalProfiles = () => {
        const { data } = this.props;
        const { usersWithPersonal } = data || {};

        return (
            <ManagedTable
                headers={headers}
                renderBody={this.renderRow}
                renderBodyKey="id"
                rows={usersWithPersonal}
                rowsPerPage={-1}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="firstName"
            />
        );
    };

    renderOtherProfiles = () => {
        const { data } = this.props;
        const { usersWithoutPersonal } = data || {};

        return (
            <ManagedTable
                headers={headers}
                renderBody={this.renderRow}
                renderBodyKey="id"
                rows={usersWithoutPersonal}
                rowsPerPage={-1}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="firstName"
            />
        );
    };

    render() {
        const { t } = this.props;
        const { loaded } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h1">
                            {t('Common.overview')}
                        </Typography>
                        <Typography paragraph mt="16px">
                            {t('Reports.browser.description')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {this.renderCharts()}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h5">
                            {t('Reports.Browser.users_with_personal')}
                        </Typography>
                        {this.renderPersonalProfiles()}
                    </Grid>
                </Grid>
                <Box paddingY="32px">
                    <Typography variant="h5">
                        {t('Reports.Browser.users_without_personal')}
                    </Typography>
                    {this.renderOtherProfiles()}
                </Box>
            </>
        );
    }
}

export default withTranslation()(Overview);
